import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { servicesData } from "../../data/servicesData";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import img from "../../assets/images/Home/slide2.jpeg"
// --- Global Theme Colors (Logo Based) ---
const COLORS = {
  accent: "#D7A19E",    // Logo ka Muted Rose (Silhouette)
  dark: "#546E7A",      // Logo ka Dark Slate Blue (Main Text)
  bgOverlay: "rgba(255, 255, 255, 0.92)", // Soft white for readability
  sageLight: "#9BB2A3", // Logo ka Sage Green (For subtle accents)
};

const ServiceCard = ({ item, index, scrollYProgress }) => {
  const xPos = useTransform(scrollYProgress, [0, 0.4], [index * 10 - 20, 0]);
  const yPos = useTransform(scrollYProgress, [0, 0.4], [index * -5, 0]);
  const rotate = useTransform(
    scrollYProgress,
    [0, 0.4],
    [index % 2 === 0 ? 5 : -5, 0]
  );
  const opacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.4], [0.9, 1]);

  return (
    <motion.div
      style={{ x: xPos, y: yPos, rotateZ: rotate, opacity, scale }}
      className="group bg-white rounded-[2.5rem] shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 p-10 flex flex-col"
    >
      {/* Accent Line */}
      <div
        className="w-12 h-1 rounded-full mb-6 mx-auto"
        style={{ backgroundColor: COLORS.accent }}
      />

      {/* Category */}
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-40 mb-3 text-center">
        {item.category}
      </span>

      {/* Title */}
      <h4
        className="text-2xl font-semibold mb-4 text-center"
        style={{ color: COLORS.dark }}
      >
        {item.title}
      </h4>

      {/* Description */}
      <p className="text-sm leading-relaxed mb-8 opacity-70 text-center">
        {item.shortDescription}
      </p>

      {/* Button */}
      <Link
        to={`/obs-gynae/${item.parentSlug}/${item.slug}`}
        className="group/btn relative flex items-center justify-center gap-3 px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all duration-300 overflow-hidden mt-auto"
        style={{
          color: COLORS.accent,
          border: `1px solid ${COLORS.accent}`,
        }}
      >
        <span className="relative z-10 transition-colors duration-300 group-hover/btn:text-white">
          Read More
        </span>

        <motion.span className="relative z-10 transition-transform duration-300 group-hover/btn:translate-x-2 group-hover/btn:text-white">
          <HiOutlineArrowNarrowRight size={18} />
        </motion.span>

        <div
          className="absolute inset-0 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 ease-out z-0"
          style={{ backgroundColor: COLORS.accent }}
        />
      </Link>
    </motion.div>
  );
};

const ObsGynae = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const pregnancyServices = servicesData.filter(
    (item) => item.category === "pregnancy"
  );

  const gynecologyServices = servicesData.filter(
    (item) => item.category === "gynecology"
  );

  return (
    <section
      ref={containerRef}
      className="relative py-24 px-6 flex flex-col items-center overflow-hidden"
      style={{
        backgroundImage:img,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Background Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ backgroundColor: COLORS.bgOverlay, backdropFilter: "blur(2px)" }}
      ></div>

      <div className="relative z-10 w-full max-w-7xl">
        {/* Main Heading */}
        <div className="text-center mb-20">
          <h2 
            className="text-4xl md:text-5xl  mb-4"
            style={{ color: COLORS.dark }}
          >
            OBS & GYNAE
          </h2>
          <p className="text-lg opacity-80" style={{ color: COLORS.dark }}>
            Advanced Obstetrics & Gynecology care for every stage of life.
          </p>
        </div>

        {/* Pregnancy Section */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold mb-12 relative inline-block" style={{ color: COLORS.dark }}>
            <span className="relative z-10">Pregnancy Care</span>
            <span
              className="absolute bottom-1 left-0 w-full h-3 -z-0"
              style={{ backgroundColor: COLORS.accent, opacity: 0.25 }}
            />
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {pregnancyServices.map((item, i) => (
              <ServiceCard
                key={item.id}
                item={item}
                index={i}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </div>
        </div>

        {/* Gynecology Section */}
        <div>
          <h3 className="text-3xl font-bold mb-12 relative inline-block" style={{ color: COLORS.dark }}>
            <span className="relative z-10">Gynecology</span>
            <span
              className="absolute bottom-1 left-0 w-full h-3 -z-0"
              style={{ backgroundColor: COLORS.sageLight, opacity: 0.25 }}
            />
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {gynecologyServices.map((item, i) => (
              <ServiceCard
                key={item.id}
                item={item}
                index={i}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `.group\\/btn:hover span { color: white !important; }`,
        }}
      />
    </section>
  );
};

export default ObsGynae;